// src/live/services/eventService.ts
import { auth, db, functions, storage } from '../../lib/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import {
  getFirestore as getFirestoreLite,
  doc as docLite,
  deleteDoc as deleteDocLite,
  updateDoc as updateDocLite,
} from 'firebase/firestore/lite';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { uploadToCfImages } from '../../lib/services/cfImagesService';
import { httpsCallable } from 'firebase/functions';
import type { LiveEvent, EventRsvp, RsvpStatus, CreateEventData, AppNotification } from '../types/events';
import { resolveLiveAudienceContract } from '../types/liveAudience';
import {
  resolveLiveArchiveMode,
  resolveLiveArchivePolicy,
} from '../types/liveArchive';
import {
  DEFAULT_LIVE_OPERATOR_POLICY,
  resolveLiveOperatorPolicy,
  type LiveModerationState,
  type LiveOperatorPolicy,
  type LiveStageRole,
} from '../types/liveOperators';
import {
  buildLiveEncoderSetup,
  getEncoderProfileById,
} from './liveEncoderService';
import {
  resolveLiveDeliveryContract,
  resolveLiveDeliveryMode,
} from '../types/liveDelivery';
import {
  getEventScheduleValidationMessage,
  hasMinimumEventLeadTime,
} from './eventSchedule';
import { createLiveRoom, endLiveRoom, type LiveRoom } from './liveService';
import { getRoomState } from './cloudflareSignaling';
import { withTimeout } from '../utils/withTimeout';
import { firestoreWriteWithFallback } from '../utils/firestoreWriteWithFallback';
import {
  assertValidPreflightConsent,
  type PreflightConsent,
} from '../lib/preflightConsent';

const EVENTS_COLLECTION = 'liveEvents';
const RSVPS_SUBCOLLECTION = 'rsvps';
const NOTIFICATIONS_COLLECTION = 'notifications';
const createEventValidatedCallable = httpsCallable(functions, 'createEventValidated_v2');
const FIRESTORE_READY_TIMEOUT_MS = 5_000;
const MAX_EVENT_THUMBNAIL_SIZE_BYTES = 10 * 1024 * 1024;
const dbLite = getFirestoreLite(auth.app);

const coerceTimestamp = (
  value: unknown,
  fallback: Timestamp = Timestamp.now(),
): Timestamp => {
  if (value instanceof Timestamp) {
    return value;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return Timestamp.fromDate(value);
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      return Timestamp.fromDate(parsedDate);
    }
  }

  if (value && typeof value === 'object') {
    const maybeTimestamp = value as {
      seconds?: unknown;
      nanoseconds?: unknown;
      toDate?: () => Date;
    };

    if (
      typeof maybeTimestamp.seconds === 'number' &&
      typeof maybeTimestamp.nanoseconds === 'number'
    ) {
      return new Timestamp(maybeTimestamp.seconds, maybeTimestamp.nanoseconds);
    }

    if (typeof maybeTimestamp.toDate === 'function') {
      try {
        const convertedDate = maybeTimestamp.toDate();
        if (convertedDate instanceof Date && !Number.isNaN(convertedDate.getTime())) {
          return Timestamp.fromDate(convertedDate);
        }
      } catch {
        // Ignore malformed timestamp-like objects and fall through to the fallback.
      }
    }
  }

  return fallback;
};

const normalizeEventModel = (event: LiveEvent): LiveEvent => {
  const createdAt = coerceTimestamp(event.createdAt);
  const schedulePendingSync = !event.scheduledAt;
  const scheduledAt = coerceTimestamp(event.scheduledAt, createdAt);
  const deliveryMode = resolveLiveDeliveryMode({
    source: 'event',
    deliveryMode: event.deliveryMode,
    deliveryContract: event.deliveryContract,
  });
  const archiveMode = resolveLiveArchiveMode({
    source: 'event',
    deliveryMode,
    archiveMode: event.archiveMode,
    archivePolicy: event.archivePolicy,
  });
  const encoderProfile = deliveryMode === 'quality'
    ? (event.encoderProfile || getEncoderProfileById(event.encoderProfileId))
    : undefined;
  const encoderSetup = deliveryMode === 'quality'
    ? (event.encoderSetup || buildLiveEncoderSetup(encoderProfile?.id, { reservedOnly: true }))
    : undefined;

  return {
    ...event,
    scheduledAt,
    schedulePendingSync,
    createdAt,
    startedAt: event.startedAt ? coerceTimestamp(event.startedAt, scheduledAt) : undefined,
    endedAt: event.endedAt ? coerceTimestamp(event.endedAt, scheduledAt) : undefined,
    audienceContract: resolveLiveAudienceContract({
      source: 'event',
      audienceContract: event.audienceContract,
    }),
    tags: Array.isArray(event.tags) ? event.tags : [],
    operatorRoles: event.operatorRoles || {},
    moderationStates: event.moderationStates || {},
    operatorPolicy: resolveLiveOperatorPolicy(event.operatorPolicy),
    archiveMode,
    archivePolicy: resolveLiveArchivePolicy({
      source: 'event',
      deliveryMode,
      archiveMode,
      archivePolicy: event.archivePolicy,
    }),
    deliveryMode,
    deliveryContract: resolveLiveDeliveryContract({
      source: 'event',
      deliveryMode,
      deliveryContract: event.deliveryContract,
    }),
    encoderProfileId: encoderProfile?.id,
    encoderProfile,
    encoderSetup,
  };
};

// WebChannel transport error detection moved to ../utils/firestoreWriteWithFallback.ts

const ensureFirestoreReady = async (timeoutMs = FIRESTORE_READY_TIMEOUT_MS): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated with Firebase Auth');
  }

  const warmup = async () => {
    await user.getIdToken(true);
    await getDoc(doc(db, 'users', user.uid));
  };

  await withTimeout(warmup(), timeoutMs, 'Firestore warmup timed out');
};

type CreateEventCallableResult = {
  eventId?: string;
};

// ============ Event CRUD ============

const sanitizeFileName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * Live thumbnail category — controls the storage subfolder so thumbnails for
 * each live mode are organized cleanly. Add new modes here as they appear.
 */
export type LiveThumbnailCategory =
  | 'live-events'
  | 'live-meetings'
  | 'live-native'
  | 'live-shorts'
  | 'live-battle'
  | 'live-audio'
  | 'live-gaming'
  | 'live-replay';

/**
 * Single source of truth for live thumbnail uploads. Used by every live form
 * (Native, Shorts, Battle, Audio, Event, Meeting) and by the replay publish
 * panel. Keep all path conventions and validation here so adding new modes is
 * a one-liner.
 */
export async function uploadLiveThumbnail(
  userId: string,
  file: File,
  category: LiveThumbnailCategory,
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Thumbnail file must be an image.');
  }
  if (file.size > MAX_EVENT_THUMBNAIL_SIZE_BYTES) {
    throw new Error('Thumbnail image must be 10MB or smaller.');
  }

  // Lazy migration to Cloudflare Images: try CF first, fall back to Firebase
  // Storage on any failure. This keeps dev environments without CF_IMAGES_TOKEN
  // working, survives CF outages, and old stored URLs remain valid forever
  // (old events keep rendering their Firebase Storage thumbnails). New uploads
  // naturally migrate to CF Images without a bulk backfill.
  try {
    const { url } = await uploadToCfImages(file, category);
    return url;
  } catch (err) {
    console.warn(
      '[uploadLiveThumbnail] CF Images upload failed — falling back to Firebase Storage:',
      err,
    );
  }

  const safeName = sanitizeFileName(file.name || 'thumbnail.jpg') || 'thumbnail.jpg';
  const storagePath = `user-uploads/${userId}/${category}/${Date.now()}-${safeName}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, { contentType: file.type });
  return getDownloadURL(storageRef);
}

/**
 * Backward-compatible wrapper used by the existing event creation flow.
 * New code should call uploadLiveThumbnail directly with the appropriate
 * category instead.
 */
export async function uploadEventThumbnail(userId: string, file: File): Promise<string> {
  return uploadLiveThumbnail(userId, file, 'live-events');
}

export async function createEvent(
  hostId: string,
  data: CreateEventData,
  preflightConsent: PreflightConsent,
): Promise<LiveEvent> {
  // T12 (LAUNCH_PLAN.md) — scheduling-time host attestation. The runtime
  // go-live attestation is captured separately in startEvent() (different
  // prompt; both persisted for audit).
  assertValidPreflightConsent(preflightConsent, hostId);

  if (!hasMinimumEventLeadTime(data.scheduledAt)) {
    throw new Error(getEventScheduleValidationMessage());
  }

  const scheduledDate = new Date(data.scheduledAt);
  const scheduledAt = scheduledDate.toISOString();
  const deliveryMode = resolveLiveDeliveryMode({
    source: 'event',
    deliveryMode: data.deliveryMode,
  });
  const archiveMode = resolveLiveArchiveMode({
    source: 'event',
    deliveryMode,
    archiveMode: data.archiveMode,
  });
  const archivePolicy = resolveLiveArchivePolicy({
    source: 'event',
    deliveryMode,
    archiveMode,
    autoPublishReplay: data.enableReplay,
  });
  const encoderProfile = deliveryMode === 'quality'
    ? getEncoderProfileById(data.encoderProfileId)
    : undefined;
  const encoderSetup = encoderProfile
    ? buildLiveEncoderSetup(encoderProfile.id, { reservedOnly: true })
    : undefined;

  try {
    const result = await createEventValidatedCallable({
      title: data.title,
      description: data.description,
      scheduledAt,
      thumbnailUrl: data.thumbnailUrl,
      enableRsvp: data.enableRsvp,
      category: data.category,
      tags: data.tags,
      allowedUsers: data.allowedUsers,
      hostName: data.hostName || '',
      hostAvatar: data.hostAvatar || null,
    });
    const callableData = result.data as CreateEventCallableResult | undefined;
    const eventId = callableData?.eventId;
    if (!eventId) {
      throw new Error('Event creation returned no event ID');
    }

    await setDoc(doc(db, EVENTS_COLLECTION, eventId), {
      archiveMode,
      archivePolicy,
      deliveryMode,
      preflightConsent,
      ...(encoderProfile ? {
        encoderProfileId: encoderProfile.id,
        encoderProfile,
        encoderSetup,
      } : {}),
    }, { merge: true }).catch((error) => {
      console.error('[eventService] Failed to persist delivery mode metadata:', error);
    });

    return normalizeEventModel({
      id: eventId,
      hostId,
      hostName: data.hostName,
      hostAvatar: data.hostAvatar || null,
      title: data.title,
      description: data.description,
      thumbnailUrl: data.thumbnailUrl,
      allowedUsers: data.allowedUsers,
      scheduledAt: Timestamp.fromDate(scheduledDate),
      status: 'upcoming',
      enableRsvp: data.enableRsvp,
      rsvpCounts: { going: 0, maybe: 0, not_going: 0 },
      createdAt: Timestamp.now(),
      category: data.category,
      tags: data.tags,
      audienceContract: resolveLiveAudienceContract({ source: 'event' }),
      operatorRoles: {},
      moderationStates: {},
      operatorPolicy: DEFAULT_LIVE_OPERATOR_POLICY,
      archiveMode,
      archivePolicy,
      deliveryMode,
      ...(encoderProfile ? {
        encoderProfileId: encoderProfile.id,
        encoderProfile,
        encoderSetup,
      } : {}),
    });
  } catch (err) {
    const code = (err as { code?: unknown })?.code;
    // Dev fallback only if callable is unavailable in this environment.
    if (code === 'functions/not-found' || code === 'functions/unimplemented') {
      const eventRef = doc(collection(db, EVENTS_COLLECTION));
      const event: LiveEvent = {
        id: eventRef.id,
        hostId,
        hostName: data.hostName,
        hostAvatar: data.hostAvatar || null,
        title: data.title,
        description: data.description,
        thumbnailUrl: data.thumbnailUrl,
        allowedUsers: data.allowedUsers,
        scheduledAt: Timestamp.fromDate(scheduledDate),
        status: 'upcoming',
        enableRsvp: data.enableRsvp,
        rsvpCounts: { going: 0, maybe: 0, not_going: 0 },
        createdAt: Timestamp.now(),
        category: data.category,
        tags: data.tags,
        audienceContract: resolveLiveAudienceContract({ source: 'event' }),
        operatorRoles: {},
        moderationStates: {},
        operatorPolicy: DEFAULT_LIVE_OPERATOR_POLICY,
        archiveMode,
        archivePolicy,
        deliveryMode,
        preflightConsent,
        ...(encoderProfile ? {
          encoderProfileId: encoderProfile.id,
          encoderProfile,
          encoderSetup,
        } : {}),
      };
      await setDoc(eventRef, event);
      return normalizeEventModel(event);
    }
    throw err;
  }
}

export async function getEvent(eventId: string): Promise<LiveEvent | null> {
  const eventDoc = await getDoc(doc(db, EVENTS_COLLECTION, eventId));
  if (!eventDoc.exists()) return null;
  return normalizeEventModel(eventDoc.data() as LiveEvent);
}

export async function getUpcomingEvents(): Promise<LiveEvent[]> {
  const q = query(
    collection(db, EVENTS_COLLECTION),
    where('status', 'in', ['upcoming', 'starting_soon']),
    orderBy('scheduledAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => normalizeEventModel(doc.data() as LiveEvent));
}

const buildEventRuntimeRoom = async (
  event: LiveEvent,
  preflightConsent: PreflightConsent,
): Promise<LiveRoom> =>
  createLiveRoom(
    event.hostId,
    {
      isRoom: true,
      title: event.title,
      description: event.description,
      thumbnailUrl: event.thumbnailUrl,
      roomType: 'event',
      isVertical: false,
      broadcastMeeting: true,
      audienceContract: resolveLiveAudienceContract({ source: 'event' }),
      archiveMode: event.archiveMode,
      archivePolicy: event.archivePolicy,
      deliveryMode: event.deliveryMode,
      deliveryContract: event.deliveryContract,
      maxParticipants: 9999,
      tags: event.tags || [],
      category: event.category || '',
      isPrivate: true,
      allowedUsers: event.allowedUsers || [],
      hostName: event.hostName,
      hostAvatar: event.hostAvatar,
    },
    {
      linkedEventId: event.id,
      suppressDiscovery: true,
      preflightConsent,
    },
  );

export async function startEvent(
  eventId: string,
  preflightConsent: PreflightConsent,
): Promise<string> {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const eventSnap = await getDoc(eventRef);
  if (!eventSnap.exists()) {
    throw new Error('Event not found');
  }

  const event = normalizeEventModel({ id: eventSnap.id, ...eventSnap.data() } as LiveEvent);
  // T12 — fail loud at runtime if the runtime go-live preflight is missing,
  // before any Worker / Firestore writes. Belt-and-braces with the assert
  // inside createLiveRoom (chokepoint), so wrappers like this can't slip a
  // forged consent through.
  assertValidPreflightConsent(preflightConsent, event.hostId);
  const existingRuntimeRoom = event.runtimeRoomId
    ? await getRoomState(event.runtimeRoomId).catch(() => null)
    : null;
  const existingRuntimeRoomId =
    event.runtimeRoomId && existingRuntimeRoom?.isActive
      ? event.runtimeRoomId
      : null;

  let runtimeRoomId = existingRuntimeRoomId;

  if (!existingRuntimeRoomId) {
    const createdRuntimeRoom = await buildEventRuntimeRoom(event, preflightConsent);
    runtimeRoomId = createdRuntimeRoom.id;
  }

  // Event replay state is runtime-room authoritative only.
  // startEvent() must not write any replay object onto liveEvents/{id}; the
  // Event doc only owns product/runtime linkage while replay lifecycle stays
  // on liveRooms/{runtimeRoomId}.recording.
  await updateDoc(eventRef, {
    status: 'live',
    startedAt: serverTimestamp(),
    runtimeRoomId,
  });
  console.log('[id_handoff] event runtime room linked on goLive', {
    stage: 'client.startEvent',
    roomId: runtimeRoomId,
    eventId,
    replaySource: `liveRooms/${runtimeRoomId}`,
    ts: Date.now(),
  });
  if (!runtimeRoomId) {
    throw new Error('Event runtime room could not be resolved.');
  }
  return runtimeRoomId;
}

export async function endEvent(
  eventId: string,
  options?: { skipRuntimeRoomEnd?: boolean },
): Promise<void> {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const eventSnap = await getDoc(eventRef).catch(() => null);
  const event = eventSnap?.exists()
    ? normalizeEventModel({ id: eventSnap.id, ...eventSnap.data() } as LiveEvent)
    : null;

  if (event?.runtimeRoomId && !options?.skipRuntimeRoomEnd) {
    await endLiveRoom(event.runtimeRoomId).catch(() => {});
  }

  await ensureFirestoreReady();
  try {
    await updateDocLite(docLite(dbLite, EVENTS_COLLECTION, eventId), {
      status: 'ended',
      endedAt: new Date(),
      viewerCount: 0,
      stageCount: 0,
    });
  } catch {
    await firestoreWriteWithFallback(
      () => updateDoc(eventRef, {
        status: 'ended',
        endedAt: serverTimestamp(),
        viewerCount: 0,
        stageCount: 0,
      }),
      () => updateDocLite(docLite(dbLite, EVENTS_COLLECTION, eventId), {
        status: 'ended',
        endedAt: new Date(),
        viewerCount: 0,
        stageCount: 0,
      }),
      { retries: 1, label: 'endEvent' },
    );
  }
  // Safety net: remove discovery card immediately even if trigger propagation lags.
  deleteDoc(doc(db, 'liveIndex', eventId)).catch(() => {});
}

export async function cancelEvent(eventId: string): Promise<void> {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  await ensureFirestoreReady();
  try {
    await deleteDocLite(docLite(dbLite, EVENTS_COLLECTION, eventId));
  } catch {
    await firestoreWriteWithFallback(
      () => deleteDoc(eventRef),
      () => deleteDocLite(docLite(dbLite, EVENTS_COLLECTION, eventId)),
      { retries: 1, label: 'cancelEvent' },
    );
  }
  // Safety net: ensure cancelled events disappear from Live grid immediately.
  deleteDoc(doc(db, 'liveIndex', eventId)).catch(() => {});
}

// ============ Spotlight Functions ============

export async function setSpotlight(eventId: string, userId: string): Promise<void> {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  await updateDoc(eventRef, {
    spotlightUserId: userId,
  });
}

export async function clearSpotlight(eventId: string): Promise<void> {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  await updateDoc(eventRef, {
    spotlightUserId: null,
  });
}

export async function setEventParticipantRole(
  eventId: string,
  participantId: string,
  role: LiveStageRole,
): Promise<void> {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const snapshot = await getDoc(eventRef);
  if (!snapshot.exists()) {
    throw new Error('Event not found');
  }

  const event = normalizeEventModel(snapshot.data() as LiveEvent);
  const nextRoles = {
    ...(event.operatorRoles || {}),
    [participantId]: role,
  };
  const nextModerationStates = {
    ...(event.moderationStates || {}),
    [participantId]: 'active' as LiveModerationState,
  };

  await updateDoc(eventRef, {
    operatorRoles: nextRoles,
    moderationStates: nextModerationStates,
  });
}

export async function updateEventOperatorPolicy(
  eventId: string,
  patch: Partial<LiveOperatorPolicy>,
): Promise<void> {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const snapshot = await getDoc(eventRef);
  if (!snapshot.exists()) {
    throw new Error('Event not found');
  }

  const event = normalizeEventModel(snapshot.data() as LiveEvent);
  await updateDoc(eventRef, {
    operatorPolicy: resolveLiveOperatorPolicy({
      ...(event.operatorPolicy || DEFAULT_LIVE_OPERATOR_POLICY),
      ...patch,
    }),
  });
}

export async function removeParticipantFromEvent(
  eventId: string,
  participantId: string,
): Promise<void> {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const participantRef = doc(db, EVENTS_COLLECTION, eventId, 'participants', participantId);
  const snapshot = await getDoc(eventRef);
  if (!snapshot.exists()) {
    throw new Error('Event not found');
  }

  const event = normalizeEventModel(snapshot.data() as LiveEvent);
  const nextAllowedUsers = (event.allowedUsers || []).filter((uid) => uid !== participantId);
  const nextRoles = { ...(event.operatorRoles || {}) };
  const nextModerationStates = {
    ...(event.moderationStates || {}),
    [participantId]: 'removed' as LiveModerationState,
  };
  delete nextRoles[participantId];

  await updateDoc(eventRef, {
    allowedUsers: nextAllowedUsers,
    operatorRoles: nextRoles,
    moderationStates: nextModerationStates,
    ...(event.spotlightUserId === participantId ? { spotlightUserId: null } : {}),
  });

  await deleteDoc(participantRef).catch(() => {});
}

// Real-time listener for event
export function subscribeToEvent(
  eventId: string,
  callback: (event: LiveEvent | null) => void
): () => void {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  return onSnapshot(eventRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(normalizeEventModel({ id: snapshot.id, ...snapshot.data() } as LiveEvent));
    } else {
      callback(null);
    }
  });
}

// ============ RSVP Functions ============

export async function setRsvp(
  eventId: string,
  userId: string,
  status: RsvpStatus
): Promise<void> {
  const rsvpRef = doc(db, EVENTS_COLLECTION, eventId, RSVPS_SUBCOLLECTION, userId);

  // Get existing RSVP to check if updating
  const existingDoc = await getDoc(rsvpRef);
  const now = Timestamp.now();

  if (existingDoc.exists()) {
    // Update existing RSVP
    await updateDoc(rsvpRef, {
      status,
      updatedAt: now,
    });
  } else {
    // Create new RSVP
    const rsvp: EventRsvp = {
      userId,
      status,
      createdAt: now,
      updatedAt: now,
    };
    await setDoc(rsvpRef, rsvp);
  }
}

export async function removeRsvp(eventId: string, userId: string): Promise<void> {
  const rsvpRef = doc(db, EVENTS_COLLECTION, eventId, RSVPS_SUBCOLLECTION, userId);
  await deleteDoc(rsvpRef);
}

export async function getUserRsvp(
  eventId: string,
  userId: string
): Promise<EventRsvp | null> {
  const rsvpRef = doc(db, EVENTS_COLLECTION, eventId, RSVPS_SUBCOLLECTION, userId);
  const rsvpDoc = await getDoc(rsvpRef);
  if (!rsvpDoc.exists()) return null;
  return rsvpDoc.data() as EventRsvp;
}

// Real-time listener for user's RSVP
export function subscribeToUserRsvp(
  eventId: string,
  userId: string,
  callback: (rsvp: EventRsvp | null) => void
): () => void {
  const rsvpRef = doc(db, EVENTS_COLLECTION, eventId, RSVPS_SUBCOLLECTION, userId);
  return onSnapshot(rsvpRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data() as EventRsvp);
    } else {
      callback(null);
    }
  });
}

// ============ Notifications ============

export function subscribeToNotifications(
  userId: string,
  callback: (notifications: AppNotification[]) => void
): () => void {
  const notificationsRef = collection(db, 'users', userId, NOTIFICATIONS_COLLECTION);
  const q = query(notificationsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as AppNotification[];
      callback(notifications);
    },
    (error) => {
      // Silently handle Firestore listener errors (e.g., during rapid mount/unmount)
      console.warn('Notification subscription error:', error.message);
    }
  );
}

export async function markNotificationRead(
  userId: string,
  notificationId: string
): Promise<void> {
  const notificationRef = doc(db, 'users', userId, NOTIFICATIONS_COLLECTION, notificationId);
  await updateDoc(notificationRef, { read: true });
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const notificationsRef = collection(db, 'users', userId, NOTIFICATIONS_COLLECTION);
  const q = query(notificationsRef, where('read', '==', false));
  const snapshot = await getDocs(q);

  const updates = snapshot.docs.map(doc =>
    updateDoc(doc.ref, { read: true })
  );
  await Promise.all(updates);
}

// ============ Layout + Recording ============

// Update event layout mode (host-controlled, stored in Firestore for viewer sync)
export async function updateEventLayoutMode(
  eventId: string,
  mode: 'grid' | 'focus',
): Promise<void> {
  const ref = docLite(dbLite, EVENTS_COLLECTION, eventId);
  await updateDocLite(ref, { layoutMode: mode });
}
